import { useState } from "react";
import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard, Key, FlaskConical,
  ScrollText, Settings,
  Atom, ChevronLeft, Bell, Search, ChevronDown,
  Coins, LogOut, ShieldCheck, Wallet, Layers, Boxes, Users,
  Globe, Sun, Moon, FlaskRound, FileText, Receipt, FileClock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";

interface ConsoleLayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
}

export default function ConsoleLayout({ children, title, subtitle, actions }: ConsoleLayoutProps) {
  const [location] = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const { t, language, setLanguage } = useLanguage();
  const { platformTheme, setPlatformTheme } = useTheme();
  const { user, logout } = useAuth();

  const isTeamAdmin = user?.role === "team_admin" || user?.is_system_admin;

  const SIDEBAR_SECTIONS = [
    {
      title: t("console.overview"),
      items: [
        { label: t("console.dashboard"), href: "/console/dashboard", icon: <LayoutDashboard size={15} /> },
        { label: t("console.apiKeys"), href: "/console/keys", icon: <Key size={15} /> },
      ],
    },
    {
      title: t("console.sciModelLib"),
      items: [
        { label: t("console.sciLLMs"), href: "/console/llms", icon: <FlaskConical size={15} /> },
        { label: t("console.scienceAgentMarketplace"), href: "/console/science-agents", icon: <FlaskRound size={15} /> },
      ],
    },
    {
      title: t("console.account"),
      items: [
        { label: t("console.experimentLogs"), href: "/console/logs", icon: <ScrollText size={15} /> },
        { label: t("console.settings"), href: "/console/settings", icon: <Settings size={15} /> },
      ],
    },
    ...(isTeamAdmin
      ? [
          {
            title: t("console.teamGroup"),
            items: [
              { label: t("console.teamManage"), href: "/console/team/members", icon: <Users size={15} /> },
              { label: t("console.teamUsage"), href: "/console/team/usage", icon: <ScrollText size={15} /> },
              { label: t("console.teamWalletTransactions"), href: "/console/team/wallet-transactions", icon: <Receipt size={15} /> },
            ],
          },
        ]
      : []),
    ...(user?.is_system_admin
      ? [
          {
            title: t("console.admin"),
            items: [
              { label: t("console.adminTeams"), href: "/console/admin/teams", icon: <Wallet size={15} /> },
              { label: "Providers", href: "/console/admin/providers", icon: <Layers size={15} /> },
              { label: t("console.adminScienceLLMs"), href: "/console/admin/science-llms", icon: <Boxes size={15} /> },
              { label: t("console.adminUsage"), href: "/console/admin/usage", icon: <ScrollText size={15} /> },
              { label: t("console.adminUsageAttempts") || "Usage Attempts", href: "/console/admin/usage-attempts", icon: <ScrollText size={15} /> },
              { label: t("console.adminBills"), href: "/console/admin/bills", icon: <FileText size={15} /> },
              { label: t("console.adminWalletTransactions"), href: "/console/admin/wallet-transactions", icon: <Receipt size={15} /> },
              { label: t("console.adminAuditLogs") || "Audit Logs", href: "/console/admin/audit-logs", icon: <FileClock size={15} /> },
            ],
          },
        ]
      : []),
  ];

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Sidebar */}
      <aside
        className={cn(
          "flex-shrink-0 flex flex-col border-r border-white/5 transition-all duration-300",
          collapsed ? "w-[60px]" : "w-[240px]"
        )}
        style={{ background: 'var(--console-sidebar-bg)' }}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-14 px-4 border-b border-white/5">
          {!collapsed && (
            <Link href="/" className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #2563EB 0%, #06B6D4 50%, #818CF8 100%)' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3h6v8l4 7H5l4-7V3z" /><path d="M9 3h6" /><circle cx="12" cy="15" r="1" fill="white" stroke="none" /></svg>
              </div>
              <span className="font-bold text-sm text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Science<span className="text-blue-400">Token</span>
              </span>
              <span className="self-start mt-0.5 text-[8px] font-bold tracking-wider uppercase leading-none px-1 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30">
                Beta
              </span>
            </Link>
          )}
          {collapsed && (
            <div className="w-7 h-7 rounded-md bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mx-auto">
              <Atom size={14} className="text-white" />
            </div>
          )}
          {!collapsed && (
            <button
              onClick={() => setCollapsed(true)}
              className="text-slate-500 hover:text-slate-300 transition-colors"
            >
              <ChevronLeft size={14} />
            </button>
          )}
        </div>

        {/* Nav Items */}
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {SIDEBAR_SECTIONS.map((section) => (
            <div key={section.title} className="mb-4">
              {!collapsed && (
                <div className="px-2 mb-1.5 text-[10px] font-semibold text-slate-600 tracking-widest">
                  {section.title}
                </div>
              )}
              {section.items.map((item) => {
                const isActive = location === item.href || location.startsWith(item.href + "/");
                const className = cn(
                  "flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-all duration-150 mb-0.5",
                  isActive
                    ? "bg-blue-500/15 text-blue-400 font-medium"
                    : "text-slate-500 hover:text-slate-200 hover:bg-white/5"
                );
                return (
                  <Link key={item.href} href={item.href}
                    className={className} title={collapsed ? item.label : undefined}>
                    <span className={cn("flex-shrink-0", isActive ? "text-blue-400" : "")}>
                      {item.icon}
                    </span>
                    {!collapsed && (
                      <>
                        <span className="flex-1 truncate">{item.label}</span>
                        {"badge" in item && typeof item.badge === "string" && (
                          <span className="text-[8px] font-bold px-1 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30 leading-none">
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* User Profile */}
        <div className={cn(
          "border-t border-white/5 p-3",
          collapsed ? "flex justify-center" : ""
        )}>
          {!collapsed ? (
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
                {user?.display_name?.charAt(0).toUpperCase() || "U"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-slate-300 truncate flex items-center gap-1.5">
                  {user?.display_name || "User"}
                  {user?.is_system_admin && (
                    <span title={t("admin.badge")} className="inline-flex items-center text-amber-400">
                      <ShieldCheck size={11} />
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-slate-600 truncate">{user?.email || ""}</div>
              </div>
              <button onClick={logout} className="text-slate-600 hover:text-slate-300 transition-colors" title="Logout">
                <LogOut size={12} />
              </button>
            </div>
          ) : (
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-xs font-bold text-white">
              {user?.display_name?.charAt(0).toUpperCase() || "U"}
            </div>
          )}
        </div>

        {/* Expand button when collapsed */}
        {collapsed && (
          <button
            onClick={() => setCollapsed(false)}
            className="p-3 text-slate-500 hover:text-slate-300 border-t border-white/5 flex justify-center"
          >
            <ChevronLeft size={14} className="rotate-180" />
          </button>
        )}
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="flex items-center justify-between h-14 px-6 border-b border-white/5 flex-shrink-0"
          style={{ background: 'var(--console-header-bg)' }}>
          <div>
            {title && (
              <h1 className="text-sm font-semibold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {title}
              </h1>
            )}
            {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-3">
            {actions}
            {/* Language Switcher */}
            <button
              onClick={() => setLanguage(language === "zh" ? "en" : "zh")}
              className="flex items-center gap-1 h-8 px-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-all"
              title={language === "zh" ? "Switch to English" : "切换到中文"}
            >
              <Globe size={14} />
              <span className="text-xs font-medium">{language === "zh" ? "中" : "EN"}</span>
            </button>
            {/* Theme Toggle */}
            <button
              onClick={() => setPlatformTheme(platformTheme === "tech" ? "fresh" : "tech")}
              className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition-all"
              title={platformTheme === "tech" ? "Switch to Light" : "Switch to Dark"}
            >
              {platformTheme === "tech" ? <Sun size={14} /> : <Moon size={14} />}
            </button>
            <button className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition-all">
              <Bell size={14} />
            </button>
            <Link href="/">
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white text-xs gap-1.5">
                {t("console.backToSite")}
              </Button>
            </Link>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
